import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  PATIENT_FAX_TAB_ID,
  PATIENT_REFERRALS_TAB_ID,
  selectPatientPanelTab,
} from '@/features/patient-workspace';
import {
  closeFaxEditor,
  closeReferralEditor,
  setPatientPanelSelectedTab,
  useCopilotOverlays,
} from '@/shared/stores';

/**
 * Router state for a link into the patient workspace that should open Follow
 * Ups for the patient being opened.
 */
export const OPEN_FOLLOW_UPS_ON_ARRIVAL = { openFollowUps: true } as const;

function requestsFollowUps(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    'openFollowUps' in state &&
    state.openFollowUps === true
  );
}

/**
 * Opens Follow Ups when the current navigation carried
 * `OPEN_FOLLOW_UPS_ON_ARRIVAL`. Call inside the patient's `PatientScopeProvider`.
 */
export function useOpenFollowUpsOnArrival(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const overlays = useCopilotOverlays();
  const requested = requestsFollowUps(location.state);

  useEffect(() => {
    if (!requested) {
      return;
    }
    overlays.open('followUps', null);
    // Drop the request so Back or a reload doesn't open Follow Ups again.
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null }
    );
  }, [requested, overlays, navigate, location.pathname, location.search, location.hash]);
}

export function usePatientWorkspaceNavigation(patientUserId: string) {
  useEffect(() => {
    setPatientPanelSelectedTab(null);
  }, [patientUserId]);

  const handleCloseFaxEditor = useCallback(() => {
    closeFaxEditor();
    selectPatientPanelTab(PATIENT_FAX_TAB_ID, {
      actor: 'clinician',
      patientUserId,
    });
  }, [patientUserId]);

  const handleCloseReferralEditor = useCallback(() => {
    closeReferralEditor();
    selectPatientPanelTab(PATIENT_REFERRALS_TAB_ID, {
      actor: 'clinician',
      patientUserId,
    });
  }, [patientUserId]);

  return { handleCloseFaxEditor, handleCloseReferralEditor };
}
